import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Priority } from '@app/shared/constants/global.enum';
import {
  LeaderTimesheetCounts,
  OpsTimesheetTrendsService,
} from './ops-timesheet-trends.service';

interface MonthOption {
  label: string;
  value: number;
  year: number;
  selected?: boolean;
}

interface MetricTile {
  key: keyof Pick<
    LeaderTimesheetCounts,
    'pendingSubmission' | 'pendingApproval' | 'approved' | 'rejected'
  >;
  label: string;
  value: number;
  tone: 'neutral' | 'warning' | 'success' | 'danger';
}

const STATUS_COLORS = {
  pendingSubmission: '#d97706',
  pendingApproval: '#2563eb',
  approved: '#003f2d',
  rejected: '#dc2626',
};

@Component({
  selector: 'app-ops-timesheet-trends',
  templateUrl: './ops-timesheet-trends.component.html',
  styleUrls: ['./ops-timesheet-trends.component.scss'],
})
export class OpsTimesheetTrendsComponent implements OnInit {
  loading = false;
  loadError = false;
  monthOptions: MonthOption[] = [];
  leaderOptions: { label: string; value: string }[] = [{ label: 'All Leaders', value: '' }];
  selectedMonth = 0;
  selectedYear = 0;
  selectedLeader = '';
  chartData: any = null;
  chartOptions: any = null;
  approvalRate = 0;
  totalContractors = 0;
  tiles: MetricTile[] = [
    { key: 'pendingSubmission', label: 'Pending Submission', value: 0, tone: 'warning' },
    { key: 'pendingApproval', label: 'Pending Approval', value: 0, tone: 'neutral' },
    { key: 'approved', label: 'Approved', value: 0, tone: 'success' },
    { key: 'rejected', label: 'Rejected', value: 0, tone: 'danger' },
  ];
  rawData: LeaderTimesheetCounts[] = [];
  priority = Priority;

  constructor(
    private readonly trendsService: OpsTimesheetTrendsService,
    private readonly router: Router,
  ) {}

  navigateToTimesheet(): void {
    this.router.navigate(['/ops-vendor-management/timesheet'], {
      state: { month: this.selectedMonth, year: this.selectedYear },
    });
  }

  ngOnInit(): void {
    this.generateLast12Months();
    const now = new Date();
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    this.selectedMonth = prev.getMonth() + 1;
    this.selectedYear = prev.getFullYear();
    this.chartOptions = this.buildChartOptions();
    this.fetchTrends();
  }

  onMonthChange(event: any): void {
    const label = typeof event === 'string' ? event : event?.value;
    const option = this.monthOptions.find((o) => o.label === label || o.value === label);
    if (!option) return;
    this.selectedMonth = option.value;
    this.selectedYear = option.year;
    this.fetchTrends();
  }

  onLeaderChange(event: any): void {
    const label = typeof event === 'string' ? event : event?.value;
    const option = this.leaderOptions.find((o) => o.label === label || o.value === label);
    this.selectedLeader = option ? option.value : '';
    this.applyFilter();
  }

  private generateLast12Months(): void {
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      this.monthOptions.push({
        label: date.toLocaleString('default', { month: 'long', year: 'numeric' }),
        value: date.getMonth() + 1,
        year: date.getFullYear(),
        selected: i === 1,
      });
    }
  }

  private fetchTrends(): void {
    this.loading = true;
    this.loadError = false;
    this.trendsService.getStatusCountsByLeader(this.selectedMonth, this.selectedYear).subscribe({
      next: (data) => {
        this.rawData = data ?? [];
        this.refreshLeaderOptions();
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  private refreshLeaderOptions(): void {
    const leaders = this.rawData.map((r) => r.leaderName);
    this.leaderOptions = [
      { label: 'All Leaders', value: '' },
      ...leaders.map((name) => ({ label: name, value: name })),
    ];
    if (this.selectedLeader && !leaders.includes(this.selectedLeader)) {
      this.selectedLeader = '';
    }
  }

  private applyFilter(): void {
    const filtered = this.selectedLeader
      ? this.rawData.filter((d) => d.leaderName === this.selectedLeader)
      : this.rawData;
    this.renderTiles(filtered);
    this.renderChart(filtered);
  }

  private renderTiles(rows: LeaderTimesheetCounts[]): void {
    const totals = rows.reduce(
      (acc, r) => {
        acc.pendingSubmission += r.pendingSubmission;
        acc.pendingApproval += r.pendingApproval;
        acc.approved += r.approved;
        acc.rejected += r.rejected;
        acc.total += r.total;
        return acc;
      },
      { pendingSubmission: 0, pendingApproval: 0, approved: 0, rejected: 0, total: 0 }
    );
    this.tiles = this.tiles.map((tile) => ({ ...tile, value: totals[tile.key] }));
    this.totalContractors = totals.total;
    const inApprovalFlow = totals.pendingApproval + totals.approved + totals.rejected;
    const actioned = totals.approved + totals.rejected;
    this.approvalRate = inApprovalFlow > 0 ? Math.round((actioned / inApprovalFlow) * 100) : 100;
  }

  private renderChart(rows: LeaderTimesheetCounts[]): void {
    const fullNames = rows.map((d) => d.leaderName);
    const labels = fullNames.map((name) => this.truncateLabel(name, 16));
    this.chartData = {
      labels,
      fullLabels: fullNames,
      datasets: [
        {
          label: 'Pending Submission',
          backgroundColor: STATUS_COLORS.pendingSubmission,
          data: rows.map((d) => d.pendingSubmission),
          stack: 'status',
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 56,
        },
        {
          label: 'Pending Approval',
          backgroundColor: STATUS_COLORS.pendingApproval,
          data: rows.map((d) => d.pendingApproval),
          stack: 'status',
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 56,
        },
        {
          label: 'Approved',
          backgroundColor: STATUS_COLORS.approved,
          data: rows.map((d) => d.approved),
          stack: 'status',
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 56,
        },
        {
          label: 'Rejected',
          backgroundColor: STATUS_COLORS.rejected,
          data: rows.map((d) => d.rejected),
          stack: 'status',
          borderRadius: 4,
          borderSkipped: false,
          maxBarThickness: 56,
        },
      ],
    };
  }

  private buildChartOptions(): any {
    return {
      indexAxis: 'x',
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 4, bottom: 0 } },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, padding: 12 },
        },
        datalabels: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items: any[]) => {
              const idx = items[0]?.dataIndex;
              const full = items[0]?.chart?.data?.fullLabels?.[idx];
              return full || items[0]?.label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: {
            autoSkip: false,
            maxRotation: 20,
            minRotation: 0,
            font: { size: 11 },
            color: '#6b7280',
            padding: 6,
          },
          categoryPercentage: 0.6,
          barPercentage: 0.85,
        },
        y: {
          stacked: true,
          beginAtZero: true,
          grid: { color: '#f3f4f6', drawBorder: false },
          ticks: { precision: 0, font: { size: 11 }, color: '#6b7280' },
        },
      },
    };
  }

  private truncateLabel(name: string, max: number): string {
    if (!name) return '';
    return name.length > max ? name.substring(0, max - 1).trimEnd() + '…' : name;
  }
}
